/**
 * The shell's mount: decides from the record, loads the layer only when it is
 * needed, and gives up if the chunk has not arrived within the deadline so a
 * failed load leaves the home as it was.
 */

import Sentry from "@/integrations/Sentry";
import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import {
  dismissFirstRun,
  useFirstRun,
  useFirstRunGeneration,
  useFirstRunLeaving,
  type FirstRunView,
} from "./decide";
import type { FirstRunProps } from "./FirstRun";

export const LOAD_DEADLINE_MS = 8_000;

type Layer = React.ComponentType<FirstRunProps>;
interface Loaded {
  value?: Layer;
  error?: unknown;
  pending?: Promise<void>;
}

const EMPTY: Loaded = {};
let loaded: Loaded | null = null;

function load(): Loaded {
  if (loaded) return loaded;
  const started: Loaded = {
    pending: import("./FirstRun").then(
      (mod) => {
        loaded = { value: mod.FirstRun };
      },
      (error: unknown) => {
        Sentry.captureException(error);
        loaded = { error };
      },
    ),
  };
  loaded = started;
  return started;
}

function useLazyLayer(wanted: boolean): Loaded {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (!wanted) return () => {};
      const entry = load();
      if (!entry.pending) return () => {};
      let live = true;
      void entry.pending.then(() => {
        if (live) onChange();
      });
      return () => {
        live = false;
      };
    },
    [wanted],
  );
  return useSyncExternalStore(subscribe, () => loaded ?? EMPTY);
}

type Open = Exclude<FirstRunView, { kind: "none" }>;

export function FirstRunSlot() {
  const decided = useFirstRun();
  // The landing write makes the record say landed before the layer has
  // left; the slot holds the view it opened on until the layer lets go.
  const exiting = useFirstRunLeaving();
  // A dev reset starts the layer over, whatever it was showing.
  const generation = useFirstRunGeneration();
  const held = useRef<{ view: Open; generation: number } | null>(null);
  if (held.current && held.current.generation !== generation)
    held.current = null;
  if (decided.kind !== "none") held.current = { view: decided, generation };
  const view: FirstRunView =
    decided.kind !== "none"
      ? decided
      : exiting && held.current
        ? held.current.view
        : decided;
  const wanted = view.kind !== "none";
  const layer = useLazyLayer(wanted);
  const failed = !!layer.error;
  const waiting = wanted && !layer.value && !failed;
  useEffect(() => {
    if (failed) dismissFirstRun();
  }, [failed]);
  useEffect(() => {
    if (!waiting) return;
    const timer = window.setTimeout(dismissFirstRun, LOAD_DEADLINE_MS);
    return () => window.clearTimeout(timer);
  }, [waiting]);
  if (!wanted || !layer.value) return null;
  return <Mounted key={generation} Layer={layer.value} view={view} />;
}

// The record keeps changing under the layer as it writes; the layer keeps the
// view it opened on.
function Mounted({ Layer, view }: { Layer: Layer; view: Open }) {
  return <Layer initial={view} />;
}
